import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import Modal from "@mui/material/Modal";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import Chip from "@mui/material/Chip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import SuiviTimeline from "../eleves/SuiviTimeline";
import TermineBlock from "./TermineBlock";
import EmailModal from "../../components/EmailModal";
import api from "../../services/api";
import { formatPlanification } from "../../utils/planification";

const MATIERES = {
  quoran: { label: "Qour'an", bg: "#e8f5e9", color: "#2e7d32" },
  arabe: { label: "Langue arabe", bg: "#e3f2fd", color: "#1565c0" },
  science: { label: "Sciences de la religion", bg: "#fff3e0", color: "#e65100" },
};

const fieldStyle = {
  width: "100%", height: "40px", padding: "0 12px",
  borderRadius: "8px", border: "1px solid #d2d6da",
  fontSize: "14px", color: "#344767", outline: "none",
  background: "#fff", boxSizing: "border-box", fontFamily: "inherit",
};

const textareaStyle = {
  ...fieldStyle, height: "auto", padding: "10px 12px", resize: "vertical",
};

function Section({ title, icon, color = "#2e7d32", children }) {
  return (
    <MDBox mb={3}>
      <MDBox display="flex" alignItems="center" gap={1} mb={2}>
        <MDBox sx={{
          width: 28, height: 28, borderRadius: "8px",
          background: `linear-gradient(135deg, ${color}, ${color}cc)`,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Icon sx={{ color: "#fff", fontSize: 14 }}>{icon}</Icon>
        </MDBox>
        <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#344767", fontSize: 15 }}>
          {title}
        </MDTypography>
      </MDBox>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={1}>
        {children}
      </Grid>
    </MDBox>
  );
}

const TYPES = {
  individuel: { label: "Cours individuel", icon: "menu_book", color: "#1565c0", gradient: "linear-gradient(135deg, #1565c0, #0d47a1)" },
  groupe: { label: "Cours de groupe", icon: "groups", color: "#6a1b9a", gradient: "linear-gradient(135deg, #6a1b9a, #4a148c)" },
  general: { label: "Général / Conférence", icon: "campaign", color: "#2e7d32", gradient: "linear-gradient(135deg, #388e3c, #2e7d32)" },
};

const valueStyle = { color: "#1a1a1a", fontSize: 15, fontWeight: 500 };

function InfoCard({ label, action, children }) {
  return (
    <MDBox p={2.5} sx={{
      background: "#fff", borderRadius: "16px", height: "100%",
      boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    }}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5} gap={1}>
        <MDTypography sx={{
          color: "#9e9e9e", fontSize: 12, fontWeight: 600,
          textTransform: "uppercase", letterSpacing: "0.08em",
        }}>
          {label}
        </MDTypography>
        {action}
      </MDBox>
      {children}
    </MDBox>
  );
}

function AvatarCircle({ prenom, nom, photo, color }) {
  return (
    <MDBox sx={{
      width: 48, height: 48, borderRadius: "50%",
      background: color, color: "#fff",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontWeight: 700, fontSize: 16, flexShrink: 0,
      overflow: "hidden", border: `2px solid ${color}44`,
    }}>
      {photo ? (
        <img src={`${photo}`} alt=""
          style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        `${prenom?.[0] || ""}${nom?.[0] || ""}`
      )}
    </MDBox>
  );
}

// ── MODAL AJOUT ABSENCE ──
function AbsenceModal({ open, onClose, cours, onSaved }) {
  const isGroupe = Boolean(cours?.groupe_id);
  const eleves = cours?.groupe?.eleves || [];

  const [form, setForm] = useState({
    eleve_id: "",
    date: new Date().toISOString().slice(0, 10),
    motif: "",
    commentaire: "",
    rattrapage_prevu: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm({
        eleve_id: !isGroupe ? cours?.eleve_id || "" : "",
        date: new Date().toISOString().slice(0, 10),
        motif: "",
        commentaire: "",
        rattrapage_prevu: "",
      });
      setError("");
    }
  }, [open, cours, isGroupe]);

  const handleSubmit = async () => {
    if (!form.date) { setError("La date est obligatoire"); return; }
    if (isGroupe && !form.eleve_id) { setError("Sélectionnez un élève"); return; }
    setSaving(true);
    try {
      await api.post("/absences", {
        cours_id: cours.id,
        eleve_id: isGroupe ? form.eleve_id : cours.eleve_id,
        date: form.date,
        motif: form.motif || null,
        commentaire: form.commentaire || null,
        rattrapage_prevu: form.rattrapage_prevu || null,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <MDBox sx={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        width: { xs: "95%", sm: 480 },
        background: "#fff", borderRadius: "20px",
        boxShadow: "0 24px 60px rgba(0,0,0,0.2)",
        outline: "none", overflow: "hidden",
      }}>
        {/* Header */}
        <MDBox px={3} py={2.5} sx={{
          background: "linear-gradient(135deg, #c62828, #e53935)",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <MDBox>
            <MDTypography variant="h6" color="white" fontWeight="bold">
              Signaler une absence
            </MDTypography>
            <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
              {isGroupe ? cours?.groupe?.nom : `${cours?.eleve?.prenom} ${cours?.eleve?.nom}`}
            </MDTypography>
          </MDBox>
          <MDBox onClick={onClose} sx={{
            width: 32, height: 32, borderRadius: "50%",
            background: "rgba(255,255,255,0.2)", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            "&:hover": { background: "rgba(255,255,255,0.35)" },
          }}>
            <Icon sx={{ color: "#fff", fontSize: 18 }}>close</Icon>
          </MDBox>
        </MDBox>

        <MDBox p={3}>
          {error && (
            <MDBox mb={2} p={1.5} sx={{ background: "#ffebee", borderRadius: "8px" }}>
              <MDTypography variant="caption" color="error">{error}</MDTypography>
            </MDBox>
          )}

          {/* Élève (si groupe) */}
          {isGroupe && (
            <MDBox mb={2}>
              <MDTypography variant="caption" fontWeight="bold" sx={{
                display: "block", mb: 0.5, textTransform: "uppercase", fontSize: 11, color: "#9e9e9e",
              }}>
                Élève *
              </MDTypography>
              <select
                value={form.eleve_id}
                onChange={(e) => setForm((p) => ({ ...p, eleve_id: e.target.value }))}
                style={fieldStyle}
              >
                <option value="">— Choisir un élève —</option>
                {eleves.map((e) => (
                  <option key={e.id} value={e.id}>{e.prenom} {e.nom}</option>
                ))}
              </select>
            </MDBox>
          )}

          {/* Date */}
          <MDBox mb={2}>
            <MDTypography variant="caption" fontWeight="bold" sx={{
              display: "block", mb: 0.5, textTransform: "uppercase", fontSize: 11, color: "#9e9e9e",
            }}>
              Date de l&apos;absence *
            </MDTypography>
            <input type="date" value={form.date}
              onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
              style={fieldStyle} />
          </MDBox>

          {/* Motif */}
          <MDBox mb={2}>
            <MDTypography variant="caption" fontWeight="bold" sx={{
              display: "block", mb: 0.5, textTransform: "uppercase", fontSize: 11, color: "#9e9e9e",
            }}>
              Motif
            </MDTypography>
            <input type="text" value={form.motif} placeholder="Ex: Maladie, voyage..."
              onChange={(e) => setForm((p) => ({ ...p, motif: e.target.value }))}
              style={fieldStyle} />
          </MDBox>

          {/* Commentaire */}
          <MDBox mb={2}>
            <MDTypography variant="caption" fontWeight="bold" sx={{
              display: "block", mb: 0.5, textTransform: "uppercase", fontSize: 11, color: "#9e9e9e",
            }}>
              Commentaire
            </MDTypography>
            <textarea value={form.commentaire} rows={3}
              placeholder="Informations complémentaires..."
              onChange={(e) => setForm((p) => ({ ...p, commentaire: e.target.value }))}
              style={textareaStyle} />
          </MDBox>

          {/* Rattrapage */}
          <MDBox mb={3}>
            <MDTypography variant="caption" fontWeight="bold" sx={{
              display: "block", mb: 0.5, textTransform: "uppercase", fontSize: 11, color: "#9e9e9e",
            }}>
              Rattrapage prévu
            </MDTypography>
            <input type="date" value={form.rattrapage_prevu}
              onChange={(e) => setForm((p) => ({ ...p, rattrapage_prevu: e.target.value }))}
              style={fieldStyle} />
          </MDBox>

          <MDBox display="flex" gap={2}>
            <MDButton variant="outlined" color="secondary" onClick={onClose} fullWidth>
              Annuler
            </MDButton>
            <MDButton variant="gradient" color="error" onClick={handleSubmit}
              disabled={saving} fullWidth>
              {saving ? "Enregistrement..." : "Enregistrer"}
            </MDButton>
          </MDBox>
        </MDBox>
      </MDBox>
    </Modal>
  );
}

function CoursDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cours, setCours] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEleve, setSelectedEleve] = useState(null);
  const [emailModal, setEmailModal] = useState(false);
  const [absenceModal, setAbsenceModal] = useState(false);
  const [absences, setAbsences] = useState([]);
  const [savingTermine, setSavingTermine] = useState(false);

  useEffect(() => {
    api.get(`/cours/${id}`)
      .then(({ data }) => {
        setCours(data);
        if (data.groupe?.eleves?.length > 0) {
          setSelectedEleve(data.groupe.eleves[0]);
        }
      })
      .finally(() => setLoading(false));
    fetchAbsences();
  }, [id]);

  const fetchAbsences = async () => {
    try {
      const { data } = await api.get(`/absences/cours/${id}`);
      setAbsences(data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteAbsence = async (absenceId) => {
    if (!window.confirm("Supprimer cette absence ?")) return;
    try {
      await api.delete(`/absences/${absenceId}`);
      fetchAbsences();
    } catch (e) {
      alert("Erreur lors de la suppression");
    }
  };

  const toggleTermine = async (valeur) => {
    setSavingTermine(true);
    try {
      await api.put(`/cours/${id}`, { chapitre_termine: valeur });
      setCours((prev) => ({ ...prev, chapitre_termine: valeur }));
    } catch (error) {
      alert(error.response?.data?.message || "Erreur lors de la sauvegarde");
    } finally {
      setSavingTermine(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce cours ?")) return;
    try {
      await api.delete(`/cours/${id}`);
      navigate("/cours");
    } catch (error) {
      alert(error.response?.data?.message || "Erreur lors de la suppression");
    }
  };

  if (loading) return (
    <DashboardLayout><DashboardNavbar />
      <MDBox pt={6} display="flex" justifyContent="center">
        <MDTypography variant="body2" color="text">Chargement...</MDTypography>
      </MDBox>
    </DashboardLayout>
  );

  if (!cours) return (
    <DashboardLayout><DashboardNavbar />
      <MDBox pt={6}>
        <MDTypography variant="body2" color="error">Cours introuvable</MDTypography>
      </MDBox>
    </DashboardLayout>
  );

  // Co-enseignants, principal en premier (repli sur l'enseignant unique pour les anciennes données)
  const coEnseignants = cours.coEnseignants?.length
    ? [...cours.coEnseignants].sort((a, b) =>
      Number(b.CoursEnseignant?.est_principal) - Number(a.CoursEnseignant?.est_principal))
    : cours.enseignant ? [{ ...cours.enseignant, CoursEnseignant: { est_principal: true } }] : [];
  const matiere = MATIERES[cours.matiere] || { label: cours.matiere, bg: "#f5f5f5", color: "#333" };
  const isGeneral = cours.type === "general";
  const isGroupe = !isGeneral && Boolean(cours.groupe_id);
  const isIndividuel = !isGeneral && !isGroupe;
  const typeInfo = TYPES[isGeneral ? "general" : isGroupe ? "groupe" : "individuel"];
  const eleves = cours.groupe?.eleves || [];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={10}>

            {/* Boutons actions */}
            <MDBox mb={3} display="flex" alignItems="center"
              justifyContent="space-between" flexWrap="wrap" gap={2}>
              <MDButton variant="outlined" color="secondary" size="small"
                onClick={() => navigate("/cours")} sx={{ borderRadius: "10px", px: 2 }}>
                <Icon sx={{ mr: 0.5 }}>arrow_back</Icon>
                <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                  Retour à la liste
                </MDBox>
              </MDButton>
              <MDBox display="flex" gap={1} flexWrap="wrap">
                {!isGeneral && (
                <MDButton variant="outlined" color="info" size="small"
                  onClick={() => setEmailModal(true)} sx={{ borderRadius: "10px" }}>
                  <Icon sx={{ mr: { xs: 0, sm: 0.5 } }}>email</Icon>
                  <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                    Envoyer un email
                  </MDBox>
                </MDButton>
                )}
                <MDButton variant="outlined" color="warning" size="small"
                  onClick={() => navigate(`/cours/${id}/modifier`)} sx={{ borderRadius: "10px" }}>
                  <Icon sx={{ mr: { xs: 0, sm: 0.5 } }}>edit</Icon>
                  <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Modifier</MDBox>
                </MDButton>
                <MDButton variant="outlined" color="error" size="small"
                  onClick={handleDelete} sx={{ borderRadius: "10px" }}>
                  <Icon sx={{ mr: { xs: 0, sm: 0.5 } }}>cancel</Icon>
                  <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Supprimer</MDBox>
                </MDButton>
              </MDBox>
            </MDBox>

            {/* ── HEADER COURS ── */}
            <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)", mb: 3, overflow: "hidden" }}>
              <MDBox py={3} px={{ xs: 2, sm: 3 }} sx={{
                background: typeInfo.gradient,
                borderRadius: "0 0 20px 20px",
              }}>
                <MDBox display="flex" alignItems="center" justifyContent="space-between"
                  flexWrap="wrap" gap={2}>
                  <MDBox display="flex" alignItems="center" gap={2}>
                    <MDBox sx={{
                      width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 }, borderRadius: "12px",
                      background: "rgba(255,255,255,0.2)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <Icon sx={{ color: "#fff", fontSize: { xs: 22, sm: 28 } }}>
                        {typeInfo.icon}
                      </Icon>
                    </MDBox>
                    <MDBox>
                      <MDTypography variant="h5" color="white" fontWeight="bold"
                        sx={{ fontSize: { xs: "1.1rem", sm: "1.5rem" } }}>
                        {isGeneral ? "Cours général / Conférence" : matiere.label}
                      </MDTypography>
                      <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap" mt={0.5}>
                        <MDBox sx={{
                          padding: "3px 12px", borderRadius: "20px",
                          background: "rgba(255,255,255,0.85)", color: typeInfo.color,
                          fontSize: 12, fontWeight: 700,
                        }}>
                          {typeInfo.label}{isGroupe && cours.groupe?.nom ? ` · ${cours.groupe.nom}` : ""}
                        </MDBox>
                        <MDTypography variant="caption" color="white" sx={{
                          opacity: 0.85, display: { xs: "none", sm: "block" },
                        }}>
                          {formatPlanification(cours) || "Planification non définie"}
                          {cours.lieu && ` · 📍 ${cours.lieu}`}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                  <MDBox sx={{
                    display: "inline-flex", alignItems: "center", gap: 0.75,
                    padding: "6px 16px", borderRadius: "20px",
                    background: cours.chapitre_termine ? "#fff" : "rgba(255,255,255,0.2)",
                    color: cours.chapitre_termine ? "#2e7d32" : "#fff",
                    fontWeight: 700, fontSize: 13, border: "1px solid rgba(255,255,255,0.4)",
                  }}>
                    {cours.chapitre_termine ? (
                      <><CheckCircleIcon sx={{ fontSize: 18 }} /> Terminé</>
                    ) : (
                      <><HourglassEmptyIcon sx={{ fontSize: 18, animation: "pulseSablier 1.6s ease-in-out infinite",
                        "@keyframes pulseSablier": {
                          "0%, 100%": { opacity: 1, transform: "scale(1)" },
                          "50%": { opacity: 0.45, transform: "scale(0.85)" },
                        } }} /> En cours</>
                    )}
                  </MDBox>
                </MDBox>
              </MDBox>

              <MDBox pt={3} pb={3} px={{ xs: 2, sm: 3 }}>
                <Grid container spacing={2}>

                  {/* Élève individuel */}
                  {isIndividuel && (
                    <Grid item xs={12} md={6}>
                      <InfoCard label="Élève">
                        <MDBox display="flex" alignItems="center" gap={2}>
                          <AvatarCircle prenom={cours.eleve?.prenom} nom={cours.eleve?.nom}
                            photo={cours.eleve?.photo} color="#1565c0" />
                          <MDBox>
                            <MDTypography sx={valueStyle}>
                              {cours.eleve?.prenom} {cours.eleve?.nom}
                            </MDTypography>
                            {cours.eleve?.email && (
                              <MDTypography variant="caption" color="text"
                                sx={{ display: { xs: "none", sm: "block" } }}>
                                ✉️ {cours.eleve.email}
                              </MDTypography>
                            )}
                            {cours.eleve?.telephone && (
                              <MDTypography variant="caption" color="text" display="block">
                                📞 {cours.eleve.telephone}
                              </MDTypography>
                            )}
                          </MDBox>
                        </MDBox>
                        <MDBox mt={2}>
                          <MDButton variant="outlined" size="small"
                            onClick={() => navigate(`/eleves/${cours.eleve_id}`)}
                            sx={{ borderColor: "#1565c0", color: "#1565c0", borderRadius: "8px", fontSize: 11 }}>
                            Voir la fiche élève →
                          </MDButton>
                        </MDBox>
                      </InfoCard>
                    </Grid>
                  )}

                  {/* Groupe */}
                  {isGroupe && (
                    <Grid item xs={12} md={6}>
                      <InfoCard label={`Groupe · ${cours.groupe?.nom || ""}`}
                        action={(
                          <MDButton variant="outlined" size="small"
                            onClick={() => navigate(`/groupes/${cours.groupe_id}`)}
                            sx={{ borderColor: "#6a1b9a", color: "#6a1b9a", borderRadius: "8px", fontSize: 10, py: 0.5 }}>
                            Voir la fiche →
                          </MDButton>
                        )}>
                        <MDBox sx={{ maxHeight: 200, overflowY: "auto" }}>
                          {eleves.map((eleve) => (
                            <MDBox key={eleve.id}
                              display="flex" alignItems="center" gap={1.5}
                              py={1} px={1.5} mb={0.5}
                              sx={{
                                borderRadius: "10px", cursor: "pointer",
                                background: selectedEleve?.id === eleve.id ? "#f5f5f5" : "#fff",
                                border: "1px solid",
                                borderColor: selectedEleve?.id === eleve.id ? "#6a1b9a" : "#eeeeee",
                                transition: "all 0.2s",
                              }}
                              onClick={() => setSelectedEleve(eleve)}>
                              <AvatarCircle prenom={eleve.prenom} nom={eleve.nom}
                                photo={eleve.photo} color="#6a1b9a" />
                              <MDBox flex={1}>
                                <MDTypography sx={{ ...valueStyle, fontSize: 14 }}>
                                  {eleve.prenom} {eleve.nom}
                                </MDTypography>
                              </MDBox>
                              <MDBox onClick={(e) => { e.stopPropagation(); navigate(`/eleves/${eleve.id}`); }}
                                sx={{
                                  width: 26, height: 26, borderRadius: "8px",
                                  border: "1px solid #e0e0e0", color: "#616161",
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  cursor: "pointer",
                                }}>
                                <Icon sx={{ fontSize: 13 }}>visibility</Icon>
                              </MDBox>
                            </MDBox>
                          ))}
                        </MDBox>
                      </InfoCard>
                    </Grid>
                  )}

                  {/* Enseignants (co-enseignants, principal en premier) */}
                  <Grid item xs={12} md={6}>
                    <InfoCard label={isGeneral ? "Enseignants / Intervenants" : "Enseignants"}>
                      <MDBox display="flex" alignItems="center" gap={2}>
                        <AvatarCircle prenom={cours.enseignant?.prenom} nom={cours.enseignant?.nom}
                          photo={cours.enseignant?.photo} color="#6a1b9a" />
                        <MDBox>
                          <MDTypography sx={valueStyle}>
                            {cours.enseignant ? `${cours.enseignant.prenom} ${cours.enseignant.nom}` : "—"}
                          </MDTypography>
                          {cours.enseignant?.email && (
                            <MDTypography variant="caption" color="text"
                              sx={{ display: { xs: "none", sm: "block" } }}>
                              ✉️ {cours.enseignant.email}
                            </MDTypography>
                          )}
                        </MDBox>
                      </MDBox>
                      {coEnseignants.length > 1 && (
                        <MDBox mt={2}>
                          {coEnseignants.map((e) => (
                            <Chip
                              key={e.id}
                              label={`${e.prenom} ${e.nom}${e.CoursEnseignant?.est_principal ? " (principal)" : ""}`}
                              size="small"
                              onClick={() => navigate(`/enseignants/${e.id}`)}
                              color={e.CoursEnseignant?.est_principal ? "secondary" : "default"}
                              variant={e.CoursEnseignant?.est_principal ? "filled" : "outlined"}
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                        </MDBox>
                      )}
                      {cours.enseignant_id && (
                        <MDBox mt={2}>
                          <MDButton variant="outlined" size="small"
                            onClick={() => navigate(`/enseignants/${cours.enseignant_id}`)}
                            sx={{ borderColor: "#6a1b9a", color: "#6a1b9a", borderRadius: "8px", fontSize: 11 }}>
                            Voir la fiche →
                          </MDButton>
                        </MDBox>
                      )}
                    </InfoCard>
                  </Grid>

                  {/* Cours général : participants */}
                  {isGeneral && (
                    <Grid item xs={12} md={6}>
                      <InfoCard label="Nombre de participants">
                        <MDBox display="flex" alignItems="center" gap={2}>
                          <MDBox sx={{
                            width: 48, height: 48, borderRadius: "50%",
                            background: "#388e3c", color: "#fff",
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                            <Icon>groups</Icon>
                          </MDBox>
                          <MDTypography sx={{ ...valueStyle, fontSize: 22, fontWeight: 600 }}>
                            {cours.nb_participants ?? "—"}
                          </MDTypography>
                        </MDBox>
                      </InfoCard>
                    </Grid>
                  )}

                  {/* Planification */}
                  <Grid item xs={12} md={isGeneral ? 6 : 12}>
                    <InfoCard label="Planification">
                      <MDTypography sx={valueStyle}>
                        {formatPlanification(cours) || "Non définie"}
                      </MDTypography>
                      {cours.lieu && (
                        <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                          📍 {cours.lieu}
                        </MDTypography>
                      )}
                    </InfoCard>
                  </Grid>

                  {/* Cours général : commentaire */}
                  {isGeneral && (
                    <Grid item xs={12} md={6}>
                      <InfoCard label="Commentaire">
                        <MDBox sx={{
                          borderLeft: "3px solid #4caf50", background: "#f8f8f8",
                          padding: "12px", borderRadius: "8px",
                        }}>
                          {cours.commentaire_general ? (
                            <MDTypography sx={{ fontSize: 14, color: "#1a1a1a", whiteSpace: "pre-wrap" }}>
                              {cours.commentaire_general}
                            </MDTypography>
                          ) : (
                            <MDTypography sx={{ fontSize: 14, color: "#9e9e9e", fontStyle: "italic" }}>
                              Aucun commentaire
                            </MDTypography>
                          )}
                        </MDBox>
                      </InfoCard>
                    </Grid>
                  )}

                  {!isGeneral && cours.examen && (
                    <Grid item xs={12}>
                      <InfoCard label="📋 Notes d'examen">
                        <MDTypography sx={{ ...valueStyle, fontWeight: 400, whiteSpace: "pre-wrap" }}>
                          {cours.examen}
                        </MDTypography>
                      </InfoCard>
                    </Grid>
                  )}

                  {/* Cours général : terminé ? */}
                  {isGeneral && (
                    <Grid item xs={12}>
                      <TermineBlock termine={!!cours.chapitre_termine} saving={savingTermine}
                        onChange={toggleTermine} objet="general" />
                    </Grid>
                  )}
                </Grid>
              </MDBox>
            </Card>

            {/* ── SECTION ABSENCES ── */}
            {!isGeneral && (
            <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)", mb: 3 }}>
              <MDBox mx={2} mt={-3} py={2.5} px={{ xs: 2, sm: 3 }} sx={{
                background: "linear-gradient(135deg, #c62828, #e53935)",
                borderRadius: "12px", boxShadow: "0 4px 20px rgba(198,40,40,0.4)",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <MDBox display="flex" alignItems="center" gap={1.5}>
                  <Icon sx={{ color: "#fff", fontSize: 22 }}>event_busy</Icon>
                  <MDBox>
                    <MDTypography variant="h6" color="white" fontWeight="bold">
                      Absences
                    </MDTypography>
                    <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
                      {absences.length} absence{absences.length > 1 ? "s" : ""} enregistrée{absences.length > 1 ? "s" : ""}
                    </MDTypography>
                  </MDBox>
                </MDBox>
                <MDButton variant="contained" size="small"
                  onClick={() => setAbsenceModal(true)}
                  sx={{
                    background: "#fff", color: "#c62828", fontWeight: 700,
                    borderRadius: "10px", "&:hover": { background: "#ffebee" },
                  }}>
                  <Icon sx={{ mr: 0.5, fontSize: 16 }}>add</Icon>
                  Signaler
                </MDButton>
              </MDBox>

              <MDBox p={3}>
                {absences.length === 0 ? (
                  <MDBox display="flex" flexDirection="column" alignItems="center" py={4} gap={1}>
                    <Icon sx={{ fontSize: 48, color: "#e0e0e0" }}>event_available</Icon>
                    <MDTypography variant="body2" color="text">
                      Aucune absence enregistrée
                    </MDTypography>
                  </MDBox>
                ) : (
                  <MDBox>
                    {absences.map((absence, index) => (
                      <MDBox key={absence.id}
                        display="flex" alignItems="flex-start" gap={2}
                        py={1.5} px={2} mb={1}
                        sx={{
                          background: index % 2 === 0 ? "#fff5f5" : "#fff",
                          borderRadius: "12px", border: "1px solid #ffcdd2",
                          transition: "all 0.2s",
                          "&:hover": { background: "#ffebee" },
                        }}>
                        {/* Avatar élève */}
                        <MDBox sx={{
                          width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                          background: "#c62828", color: "#fff",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontWeight: 700, fontSize: 13, overflow: "hidden",
                        }}>
                          {absence.eleve?.photo ? (
                            <img src={absence.eleve.photo} alt=""
                              style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          ) : (
                            `${absence.eleve?.prenom?.[0] || ""}${absence.eleve?.nom?.[0] || ""}`
                          )}
                        </MDBox>

                        {/* Infos */}
                        <MDBox flex={1}>
                          <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#344767" }}>
                              {absence.eleve?.prenom} {absence.eleve?.nom}
                            </MDTypography>
                            <MDBox sx={{
                              padding: "1px 8px", borderRadius: "8px",
                              background: "#ffebee", color: "#c62828",
                              fontSize: 11, fontWeight: 600,
                            }}>
                              📅 {new Date(absence.date).toLocaleDateString("fr-FR")}
                            </MDBox>
                            {absence.rattrapage_prevu && (
                              <MDBox sx={{
                                padding: "1px 8px", borderRadius: "8px",
                                background: "#e8f5e9", color: "#2e7d32",
                                fontSize: 11, fontWeight: 600,
                              }}>
                                🔄 Rattrapage : {new Date(absence.rattrapage_prevu).toLocaleDateString("fr-FR")}
                              </MDBox>
                            )}
                          </MDBox>
                          {absence.motif && (
                            <MDTypography variant="caption" color="text" display="block" mt={0.3}>
                              📝 {absence.motif}
                            </MDTypography>
                          )}
                          {absence.commentaire && (
                            <MDTypography variant="caption" color="text" display="block" mt={0.3}
                              sx={{ fontStyle: "italic" }}>
                              {absence.commentaire}
                            </MDTypography>
                          )}
                        </MDBox>

                        {/* Supprimer */}
                        <MDBox onClick={() => handleDeleteAbsence(absence.id)} sx={{
                          width: 28, height: 28, borderRadius: "8px", flexShrink: 0,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          cursor: "pointer", color: "#e0e0e0",
                          "&:hover": { background: "#ffebee", color: "#c62828" },
                        }}>
                          <Icon sx={{ fontSize: 16 }}>delete</Icon>
                        </MDBox>
                      </MDBox>
                    ))}
                  </MDBox>
                )}
              </MDBox>
            </Card>
            )}

            {/* Suivi des chapitres */}
            {!isGeneral && (cours.eleve_id || selectedEleve) && (
              <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
                <MDBox mx={2} mt={-3} py={2.5} px={{ xs: 2, sm: 3 }} sx={{
                  background: "linear-gradient(135deg, #1976d2, #1565c0)",
                  borderRadius: "12px", boxShadow: "0 4px 20px rgba(21,101,192,0.4)",
                }}>
                  <MDBox display="flex" alignItems="center" gap={1.5}>
                    <Icon sx={{ color: "#fff", fontSize: 22 }}>timeline</Icon>
                    <MDBox>
                      <MDTypography variant="h6" color="white" fontWeight="bold">
                        Suivi des chapitres
                      </MDTypography>
                      <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
                        {isGroupe
                          ? `${selectedEleve?.prenom} ${selectedEleve?.nom}`
                          : `${cours.eleve?.prenom} ${cours.eleve?.nom}`}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </MDBox>
                <MDBox pt={3} pb={3} px={{ xs: 2, sm: 3 }}>
                  {isGroupe && eleves.length > 0 && (
                    <MDBox display="flex" gap={1} flexWrap="wrap" mb={3}>
                      {eleves.map((eleve) => (
                        <MDBox key={eleve.id} onClick={() => setSelectedEleve(eleve)} sx={{
                          padding: "6px 16px", borderRadius: "20px", cursor: "pointer",
                          border: "1px solid",
                          borderColor: selectedEleve?.id === eleve.id ? "#1565c0" : "#d2d6da",
                          background: selectedEleve?.id === eleve.id ? "#1565c0" : "#fff",
                          color: selectedEleve?.id === eleve.id ? "#fff" : "#344767",
                          fontSize: 13, fontWeight: 500, transition: "all 0.2s",
                        }}>
                          {eleve.prenom} {eleve.nom}
                        </MDBox>
                      ))}
                    </MDBox>
                  )}
                  <SuiviTimeline
                    eleveId={isGroupe ? selectedEleve?.id : cours.eleve_id}
                    coursId={cours.id}
                    enseignantDefaut={cours.enseignant}
                    onLivreTermineChange={(valeur) => setCours((prev) => ({ ...prev, chapitre_termine: valeur }))}
                  />
                </MDBox>
              </Card>
            )}

          </Grid>
        </Grid>
      </MDBox>

      <AbsenceModal
        open={absenceModal}
        onClose={() => setAbsenceModal(false)}
        cours={cours}
        onSaved={fetchAbsences}
      />

      <EmailModal
        open={emailModal}
        onClose={() => setEmailModal(false)}
        eleveId={!isGroupe ? cours.eleve_id : null}
        groupeId={isGroupe ? cours.groupe_id : null}
        nomDestinataire={isGroupe
          ? `groupe "${cours.groupe?.nom}"`
          : `${cours.eleve?.prenom} ${cours.eleve?.nom}`}
        elevesGroupe={isGroupe ? eleves : null}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default CoursDetail;